import React, { useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Box, Flex, Spinner, Text } from '@chilipiper/design-system'
import { getClientTimezone } from '@chilipiper/date-time'
import { useFindPersonalDetailsByIds } from '@chilipiper/api-queries/src/personal-details'
import {
  LinkButton,
  Icon,
  TimezoneSelect,
  AlertBanner,
  Section,
} from '@chilipiper/design-system/src/new'
import * as v from 'valibot'
import { SetAttendee } from '@chilipiper/api-client/src/meetings'
import { MeetingInvite, PublicMeetingType } from '@chilipiper/api-client/src/meeting-types'
import { DataFieldDataType } from '@chilipiper/api-client/src/chili-crm'
import { getUserNameOrEmail } from '@chilipiper/admin-components'
import { IconError } from '@chilipiper/icons/figma-generated/src/icon'
import {
  SelectTimeSlotParams,
  MeetingStartTimes,
  AvailabilityOptions,
  useReschedule,
  GuestFormValues,
} from '@chilipiper/booking-flow'
import { useTranslations } from '../../../context/TranslationContext'
import { useMessageFeedContext } from '../../../chat-widget/MessageFeedContext'
import { useMeeting, useMeetingQueryOptions } from '../../../api-queries/chat-platform-chat'
import { BookMeetingBlock as BookMeetingBlockType } from '../../../chat-data/types'
import { chatPublicEventAction, chiliChatEventName } from '../../../constants'
import { StyledBookMeetingContainer } from './styles'
import { BookingMeeting } from './BookingMeeting'
import { ScheduleHeader } from './components/ScheduleHeader'
import { BookingConfirmation } from './BookingConfirmation'
import { Timeout } from './Timeout'
import { ErrorSchema } from './helpers'
import { PreviousSchedule } from './PreviousSchedule'
import { useStartTimes } from './hooks'

interface Props {
  isRescheduling: boolean
  onReschedulingChange: (isRescheduling: boolean) => void
  meetingType?: PublicMeetingType
  block: Omit<BookMeetingBlockType, 'assignees'> & Partial<Pick<BookMeetingBlockType, 'assignees'>>
  fields: Array<{
    dataType?: DataFieldDataType
    description?: string
    error?: string
    id: string
    initialValue?: string
    hiddenValue?: string
    isRequired: boolean
    label?: string
  }>
  isBookingMeeting: boolean
  isNoAvailableAssignee?: boolean
  meetingInvite?: MeetingInvite
  isMeetingReserved: boolean
  claimMeetingAttemptReservationError: unknown
  refetchAvailability: () => void
  isLoading: boolean
  timeSlots?: MeetingStartTimes
  onBookMeeting: (
    data: SelectTimeSlotParams & {
      guestFormData: GuestFormValues
      onError: () => void
      timezone: string
    }
  ) => void
  guestEmail?: string
  hostId?: string
  error?: string
  suggestedTimes?: Array<string>
  initialTimezone?: string
  availabilityOptions?: AvailabilityOptions
}

export const BookingFlowBlock = ({
  block,
  hostId,
  suggestedTimes,
  onBookMeeting,
  isBookingMeeting,
  isMeetingReserved,
  meetingType,
  guestEmail,
  refetchAvailability,
  timeSlots,
  isLoading,
  isNoAvailableAssignee,
  meetingInvite,
  claimMeetingAttemptReservationError,
  fields,
  error,
  isRescheduling,
  onReschedulingChange,
  initialTimezone,
  availabilityOptions,
}: Props) => {
  const meetingId = block.meetingId
  const queryClient = useQueryClient()
  const { tenantId = '', orgDetails, isSessionExpired } = useMessageFeedContext()
  const { data: meeting } = useMeeting(tenantId, meetingId)
  const providedHostId = meeting?.hostId || hostId
  const users = useAssignees(providedHostId ? [providedHostId] : [])
  const {
    rescheduleMeeting,
    isLoading: isLoadingRescheduleMeeting,
    error: rescheduleMeetingError,
  } = useReschedule(tenantId, availabilityOptions, meetingType, meeting)
  const { startTimes, ignoreSuggestedTimes, toggleIgnoreSuggestedTimes } = useStartTimes(
    timeSlots,
    suggestedTimes,
    meeting?.dateTime?.start
  )

  const meetingQueryOptions = useMeetingQueryOptions()
  const [bookingLoader, showFullCalendar, showOnlySuggestedTimes] = useTranslations([
    'bookingLoader',
    'showFullCalendar',
    'showOnlySuggestedTimes',
  ])
  const [selectedSlotParams, setSelectedSlotParams] = useState<SelectTimeSlotParams>()
  const [timezone, setTimezone] = useState(initialTimezone ?? getClientTimezone())
  const daysWrapperRef = useRef<HTMLDivElement>(null)

  const isWaitingForMeetingId = isMeetingReserved && !meetingId
  const isBooking = isBookingMeeting || isWaitingForMeetingId || isLoadingRescheduleMeeting

  const handleBookMeeting = ({
    guestFormData,
    slotParams,
  }: {
    guestFormData: GuestFormValues
    slotParams: SelectTimeSlotParams
  }) => {
    return onBookMeeting({
      ...slotParams,
      onError: refetchAvailability,
      timezone,
      guestFormData,
    })
  }
  const handleRescheduleMeeting = async (guestEmail?: string) => {
    if (!selectedSlotParams?.slot || !meetingInvite?.duration || !guestEmail) {
      return
    }

    try {
      rescheduleMeeting({
        slot: selectedSlotParams.slot,
        userIds: selectedSlotParams.userIds,
        availableRoundRobinAssignees: selectedSlotParams.availableRoundRobinAssignees,
      }).then(response => {
        window.dispatchEvent(
          new CustomEvent(chiliChatEventName, {
            detail: {
              action: chatPublicEventAction.RESCHEDULED,
              args: { time: selectedSlotParams.slot, timezone, duration: meetingInvite?.duration },
            },
          })
        )
        const queryKey = meetingQueryOptions(tenantId, meetingId ?? '').queryKey
        if (response?.type === 'Accepted') {
          queryClient.cancelQueries({
            queryKey,
          })
          queryClient.setQueryData(queryKey, previousData => {
            if (previousData) {
              const newAttendees: SetAttendee = [
                ...(previousData.attendees?.filter(
                  attendee => attendee.value !== previousData.hostId
                ) ?? []),
                {
                  type: 'User',
                  value: response.assigneeId ?? previousData.hostId,
                  responseStatus: 'NeedsAction',
                  optional: false,
                },
              ]
              return {
                ...previousData,
                hostId: response.assigneeId ?? previousData.hostId,
                attendees: newAttendees,
              }
            }
          })
        } else {
          queryClient.invalidateQueries({ queryKey })
        }
        onReschedulingChange(false)
        refetchAvailability()
      })
    } catch (error) {
      console.error(error)
    }
  }

  const host = users.find(user => user.id === providedHostId)
  const state = useMemo(() => {
    if (isRescheduling) {
      return 'rescheduling'
    }
    if (meetingId !== undefined) {
      return 'scheduled'
    }
    return 'scheduling'
  }, [isRescheduling, meetingId])

  return (
    <Box w='100%' data-test-id='WidgetBookMeetingBlock' inert={isSessionExpired}>
      <StyledBookMeetingContainer>
        {(() => {
          if (error) {
            return (
              <Flex p={7} my={1} justifyContent='center' alignItems='center' gap={2}>
                <Icon icon={IconError} color='fg/caution' />
                <Text color='fg/caution' textStyle='body-monoline'>
                  {error}
                </Text>
              </Flex>
            )
          }

          if (isBooking) {
            return (
              <Flex p={7} my={1} justifyContent='center' alignItems='center' gap={2}>
                <Spinner />
                <Text color='fg/moderate' textStyle='body-monoline'>
                  {bookingLoader ?? 'Booking meeting'}
                </Text>
              </Flex>
            )
          }

          return (
            <>
              {state === 'scheduling' && !isLoading && <Timeout timeout={block.timeout} />}
              {state !== 'scheduled' && (
                <Banners
                  bookedTime={meeting?.dateTime}
                  timezone={timezone}
                  isNoAvailableAssignee={isNoAvailableAssignee}
                  isSlotTaken={
                    isSlotTaken(rescheduleMeetingError) ||
                    isSlotTaken(claimMeetingAttemptReservationError)
                  }
                />
              )}
              {state !== 'scheduled' && (
                <TimezoneSelect
                  label={
                    <TimezoneSelect.Label position='hidden'>
                      Preferred timezone
                    </TimezoneSelect.Label>
                  }
                  size='S'
                  variant='nested'
                  hasTime
                  value={timezone}
                  onChange={setTimezone}
                />
              )}
              <ScheduleHeader
                state={state}
                assignee={
                  host ?? {
                    id: tenantId,
                    image: orgDetails?.logo,
                    name: orgDetails?.name ?? '',
                  }
                }
                displayName={(() => {
                  if (host?.name) {
                    return host.name
                  }
                  return orgDetails?.name
                })()}
                durationInMinutes={getMilliseconds(meetingInvite?.duration) / 60 / 1000}
                onNextClick={() => {
                  daysWrapperRef.current?.scrollBy({
                    left: daysWrapperRef.current.offsetWidth - 16,
                    behavior: 'smooth',
                  })
                }}
                onPreviousClick={() => {
                  daysWrapperRef.current?.scrollBy({
                    left: -daysWrapperRef.current.offsetWidth + 16,
                    behavior: 'smooth',
                  })
                }}
              />
              {state !== 'scheduled' ? (
                <BookingMeeting
                  suggestedTimesToggle={
                    suggestedTimes ? (
                      <LinkButton variant='minor' onClick={toggleIgnoreSuggestedTimes}>
                        {ignoreSuggestedTimes
                          ? (showOnlySuggestedTimes ?? 'Show only suggested times')
                          : (showFullCalendar ?? 'Show full calendar')}
                      </LinkButton>
                    ) : undefined
                  }
                  fields={
                    isRescheduling
                      ? []
                      : fields.filter(field => {
                          return field.id !== 'PersonEmail' || !guestEmail
                        })
                  }
                  isLoading={isLoading}
                  availabilityOptions={availabilityOptions}
                  timeSlots={startTimes}
                  meetingTypeId={block.meetingTypeId}
                  guestEmail={guestEmail}
                  timezone={timezone}
                  cancelRescheduling={
                    isRescheduling ? () => onReschedulingChange(false) : undefined
                  }
                  onSchedule={guestFormData => {
                    if (isRescheduling) {
                      return handleRescheduleMeeting(guestEmail)
                    }
                    if (selectedSlotParams?.slot && meetingInvite?.locations.default) {
                      handleBookMeeting({
                        guestFormData,
                        slotParams: selectedSlotParams,
                      })
                    }
                  }}
                  onSelectSlot={setSelectedSlotParams}
                  selectedTime={selectedSlotParams?.slot}
                  daysWrapperRef={daysWrapperRef}
                />
              ) : (
                <BookingConfirmation
                  meeting={meeting}
                  timezone={timezone}
                  enableReschedule={!!startTimes.startTimes?.length}
                  onClick={() => onReschedulingChange(true)}
                  email={block.email}
                />
              )}
            </>
          )
        })()}
      </StyledBookMeetingContainer>
    </Box>
  )
}

const Banners = ({
  bookedTime,
  isSlotTaken,
  isNoAvailableAssignee,
  timezone,
}: {
  bookedTime?: { end: string; start: string }
  isSlotTaken: boolean
  isNoAvailableAssignee: boolean | undefined
  timezone: string
}) => {
  const [timeslotSelectionIsNoLongerAvailable] = useTranslations([
    'timeslotSelectionIsNoLongerAvailable',
  ])
  if (bookedTime || isSlotTaken) {
    return (
      <Flex flexDirection='column' gap={2} p={3} boxShadow='border/row'>
        {bookedTime !== undefined && <PreviousSchedule date={bookedTime} timezone={timezone} />}
        {(isSlotTaken || isNoAvailableAssignee) && (
          <Section>
            <AlertBanner
              variant='warning'
              title={
                timeslotSelectionIsNoLongerAvailable ??
                'Timeslot selection is no longer available. Please select a new one.'
              }
            />
          </Section>
        )}
      </Flex>
    )
  }
}

/**
 * Parse time with milliseconds unit to number
 *
 * @example
 *   '1000 milliseconds' => 1000
 *   undefined => 0
 */
const getMilliseconds = (time?: string) => {
  const [milliseconds] = time?.split(' ') ?? ['0']
  return Number(milliseconds)
}

const isSlotTaken = (reservationError: unknown) => {
  const { success, output } = v.safeParse(ErrorSchema, reservationError)
  return Boolean(
    success &&
      (output.error.errorCode === 'user-availability-service.slot-is-taken' ||
        output.error.errorCode === 'user-availability-service.slot-is-unavailable')
  )
}

const useAssignees = (userIds: string[]) => {
  const { tenantId = '' } = useMessageFeedContext()
  const { data: assignees } = useFindPersonalDetailsByIds(tenantId, userIds)

  return Object.entries(assignees ?? {})?.map(([assigneeId, assignee]) => ({
    id: assigneeId,
    name: getUserNameOrEmail(assignee),
    image: assignee?.imageUrl,
  }))
}
